import React from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaUserShield, FaDatabase, FaLock, FaHistory, FaShare, FaUserCog, FaGlobe, FaEdit, FaExclamationTriangle } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const PrivacyPolicy = () => {
    const styles = {
        container: {
            maxWidth: '1000px',
            padding: '3rem 1rem',
            textAlign: 'left',
        },
        header: {
            textAlign: 'center',
            marginBottom: '3rem',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#2C5282',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
        },
        lastUpdated: {
            color: '#718096',
            fontSize: '0.95rem',
            marginBottom: '2rem',
        },
        section: {
            marginBottom: '3rem',
            background: '#ffffff',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        },
        sectionTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#2D3748',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        sectionIcon: {
            color: '#4299E1',
            fontSize: '1.5rem',
        },
        subTitle: {
            fontSize: '1.2rem',
            fontWeight: '600',
            color: '#4A5568',
            marginTop: '1.5rem',
            marginBottom: '1rem',
        },
        text: {
            color: '#4A5568',
            lineHeight: '1.7',
            fontSize: '1rem',
        },
        list: {
            listStyle: 'none',
            padding: 0,
            margin: '1rem 0',
        },
        listItem: {
            marginBottom: '0.75rem',
            paddingLeft: '1.5rem',
            position: 'relative',
            '&::before': {
                content: '"•"',
                color: '#4299E1',
                position: 'absolute',
                left: 0,
            },
        },
        link: {
            color: '#4299E1',
            textDecoration: 'none',
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#2B6CB0',
            },
        },
        disclaimer: {
            background: '#FEF5F5',
            border: '1px solid #FED7D7',
            borderRadius: '8px',
            padding: '1.5rem',
            marginTop: '2rem',
        },
        disclaimerTitle: {
            color: '#C53030',
            fontWeight: '600',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        disclaimerIcon: {
            color: '#C53030',
            fontSize: '1.2rem',
        },
        contact: {
            background: '#F7FAFC',
            borderRadius: '8px',
            padding: '1.5rem',
            marginTop: '1rem',
        },
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1,
            },
        },
    };

    const sectionVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <>
            <Helmet>
                <title>DNACloudHub | Privacy Policy</title>
                <meta name="description" content="Read the privacy policy of DNACloudHub to understand how we protect your data and ensure privacy while using our cloud-based platform for running qpAdm models and genetic analysis." />
                <meta property="og:title" content={'DNACloudHub | Pricing'} />
                <meta property="og:description" content={'"Read the privacy policy of DNACloudHub to understand how we protect your data and ensure privacy while using our cloud-based platform for running qpAdm models and genetic analysis."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/privacy_policy`} />
            </Helmet>

            <Container style={styles.container}>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <div style={styles.header}>
                        <motion.h1 style={styles.title} variants={sectionVariants}>
                            <FaShieldAlt size={32} color="#2C5282" />
                            Privacy Policy
                        </motion.h1>
                        <p style={styles.lastUpdated}>Last Updated: 21st December 2024</p>
                    </div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaUserShield style={styles.sectionIcon} />
                            1. Information We Collect
                        </h2>
                        <h3 style={styles.subTitle}>Personal Identification Information:</h3>
                        <ul style={styles.list}>
                            {['First Name', 'Last Name', 'Username', 'Email Address', 'Role', 'Subscription Plan', 'Password', 'Ethnicity', 'Country'].map((item, index) => (
                                <li key={index} style={styles.listItem}>{item}</li>
                            ))}
                        </ul>

                        <h3 style={styles.subTitle}>Usage Data:</h3>
                        <ul style={styles.list}>
                            {['IP address', 'Device type and browser information', 'Pages you visit', 'Date and time of your visits', 'Actions taken within the Service'].map((item, index) => (
                                <li key={index} style={styles.listItem}>{item}</li>
                            ))}
                        </ul>

                        <h3 style={styles.subTitle}>Cookies and Tracking Technologies:</h3>
                        <p style={styles.text}>We use cookies to improve your experience, analyze usage patterns, and enhance our service. You may control cookie settings through your browser preferences.</p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaDatabase style={styles.sectionIcon} />
                            2. How We Use Your Information
                        </h2>
                        <ul style={styles.list}>
                            <li style={styles.listItem}><strong>Account Management:</strong> To create and manage your account, and to provide you with the requested services.</li>
                            <li style={styles.listItem}><strong>Communication:</strong> To send you updates, notifications, and support messages.</li>
                            <li style={styles.listItem}><strong>Service Improvement:</strong> To analyze usage data and improve our services.</li>
                            <li style={styles.listItem}><strong>Billing:</strong> To process payments and manage subscriptions.</li>
                            <li style={styles.listItem}><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights.</li>
                        </ul>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaLock style={styles.sectionIcon} />
                            3. How We Protect Your Information
                        </h2>
                        <p style={styles.text}>We implement robust security measures to protect your personal data from unauthorized access, loss, or misuse. While we use industry-standard protection methods, no internet transmission is completely secure.</p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaHistory style={styles.sectionIcon} />
                            4. Data Retention
                        </h2>
                        <p style={styles.text}>We retain your data as long as necessary for service provision or legal requirements. You may request data deletion through our contact channels.</p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaShare style={styles.sectionIcon} />
                            5. Sharing Your Information
                        </h2>
                        <p style={styles.text}>We never sell your personal data. Information sharing is limited to:</p>
                        <ul style={styles.list}>
                            <li style={styles.listItem}><strong>Service Providers:</strong> For essential services like hosting and payments</li>
                            <li style={styles.listItem}><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
                            <li style={styles.listItem}><strong>Business Transfers:</strong> In case of merger or acquisition</li>
                        </ul>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaUserCog style={styles.sectionIcon} />
                            6. Your Rights and Choices
                        </h2>
                        <ul style={styles.list}>
                            <li style={styles.listItem}><strong>Access and Correction</strong> of your personal data</li>
                            <li style={styles.listItem}><strong>Data Deletion</strong> upon request</li>
                            <li style={styles.listItem}><strong>Marketing Communications</strong> opt-out options</li>
                        </ul>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaGlobe style={styles.sectionIcon} />
                            7. Third-Party Websites
                        </h2>
                        <p style={styles.text}>We are not responsible for external websites' privacy practices. Please review their policies before sharing information.</p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaEdit style={styles.sectionIcon} />
                            8. Changes to This Policy
                        </h2>
                        <p style={styles.text}>We may update this policy periodically. Check this page for the latest version.</p>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            9. Contact Us
                        </h2>
                        <div style={styles.contact}>
                            <p style={styles.text}>For questions or concerns about your privacy:</p>
                            <p style={styles.text}><strong>Email:</strong> admin@dnacloudhub.com</p>
                            <p style={styles.text}><strong>Website:</strong> <a href="/contact_us" style={styles.link}>DNACloudHub.com Contact Page</a></p>
                        </div>
                    </motion.div>

                    <motion.div style={styles.section} variants={sectionVariants}>
                        <h2 style={styles.sectionTitle}>
                            <FaExclamationTriangle style={styles.sectionIcon} />
                            10. Disclaimer
                        </h2>
                        <div style={styles.disclaimer}>
                            <h4 style={styles.disclaimerTitle}>
                                <FaExclamationTriangle style={styles.disclaimerIcon} />
                                Responsibility for Results
                            </h4>
                            <p style={styles.text}>
                                <strong>Important:</strong> DNACloudHub provides hosting services for custom qpAdm models and does not take responsibility for the accuracy or results of the analyses performed using our platform. The results depend entirely on your data and models. We do not guarantee specific outcomes or interpretations.
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            </Container>
        </>
    );
};

export default PrivacyPolicy;
